import React from "react";
import Layout from "../../components/layout/layout";
import { graphql, PageProps } from "gatsby";
import {
  ContentPostPageQuery,
  SeoSliceFragment,
  LocalesSliceFragment,
} from "../../types/graphql";
import HeroContentPost from "../../components/hero-content-post/hero-content-post";
import HeroContentPostDownload from "../../components/hero-content-post-download/hero-content-post-download";
import AuthorInfo from "../../components/author-info/author-info";
import {
  SliceContent,
  Content,
  SliceWrapper,
  FloatingAside,
  Sticky,
  NarrowSliceWrapper,
} from "./content-post.styled";
import VerticalStoriesList from "../../components/vertical-stories-list/vertical-stories-list";
import { contentPostReadTime, linkTo } from "../../utils/contentUtils";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";
import { BreadCrumbProps } from "../../components/breadcrumbs/breadcrumbs";
import ArticleMetaData from "./article-meta-data";
import { ILocalesProps } from "../../components/locales/locales";
import { ISeoProps } from "../../components/seo/seo";
import { getPublicationDate, getEditedDate } from "../../utils/contentUtils";
import {
  SharedSlice,
  SharedSliceFragment,
  SharedSliceType,
  WrapperType,
} from "../../components/shared-slice/shared-slice";
import { IPageContext, ILocationStateProps } from "../../types/types";

export const query = graphql`
  query ContentPostPage($uid: String!, $lang: String!) {
    prismicContentPost(uid: { eq: $uid }, lang: { eq: $lang }) {
      ...ContentPost
    }
    prismicContentHub {
      ...ContentHub
    }
    allPrismicContentPost(sort: { fields: { edited_date: DESC } }, limit: 3) {
      nodes {
        ...ContentPost
      }
    }
  }
`;

const ContentPost = (
  props: PageProps<ContentPostPageQuery, IPageContext, ILocationStateProps>
) => {
  const contentPost = props.data.prismicContentPost;
  const contentPostData = contentPost?.data;
  const storiesList = props.data.allPrismicContentPost?.nodes;
  const contentHub = props.data.prismicContentHub;
  const slices = contentPostData?.body1;
  const seoSlices = contentPostData?.body;

  const publicationDate = contentPost && getPublicationDate(contentPost);
  const editedDate = contentPost && getEditedDate(contentPost);

  let pageLocale: ILocalesProps = {};
  let seo: ISeoProps = {};

  seoSlices?.forEach((slice) => {
    if (slice?.slice_type == "page_locales") {
      const pageLocales = slice as LocalesSliceFragment;
      pageLocale = {
        ...pageLocales,
        northAmericaHref: props.location.href,
      };
    } else if (slice?.slice_type == "seo_") {
      const seoSlice = slice as SeoSliceFragment;
      seo = {
        meta_title: seoSlice.primary?.meta_title,
        meta_description: seoSlice.primary?.meta_description,
        title: contentPostData?.title,
        description: contentPostData?.subtitle,
        image: contentPostData?.image?.url,
        lang: contentPost?.lang,
      };
    }
  });

  const readTime = contentPost && contentPostReadTime(contentPost);
  const isDownload = contentPostData?.content_type == "Download";
  const breadcrumbs: BreadCrumbProps[] =
    (contentHub && [
      {
        name: "Resources",
        url: linkTo(contentHub),
      },
    ]) ||
    [];

  const category = contentPostData?.category?.document;
  const categoryTitle =
    category && "data" in category && category?.data?.category_title;
  const categoryLink = category && "data" in category && linkTo(category);
  const link = contentPost && linkTo(contentPost);
  const authorDoc = contentPostData?.author?.document;
  const author = (authorDoc && "data" in authorDoc && authorDoc.data) || null;
  const editorDoc = contentPostData?.editor?.document;
  const editor = (editorDoc && "data" in editorDoc && editorDoc.data) || null;

  categoryTitle &&
    categoryLink &&
    breadcrumbs.push({
      name: categoryTitle,
      url: categoryLink,
    });

  contentPostData?.title &&
    link &&
    breadcrumbs.push({
      name: contentPostData?.title,
      url: link,
    });

  return (
    contentPost && (
      <Layout isGuttered={false} seo={seo} locales={pageLocale}>
        <ArticleMetaData contentPost={contentPost} />
        {(isDownload && (
          <HeroContentPostDownload
            heroContentPost={contentPost}
            breadcrumbs={breadcrumbs}
          />
        )) || (
          <HeroContentPost
            heroContentPost={contentPost}
            author={author}
            editor={editor}
            readTime={readTime}
            breadcrumbs={breadcrumbs}
            publicationDate={publicationDate}
            editedDate={editedDate}
          />
        )}
        <Content className="content" isDownload={isDownload}>
          <SliceContent className="main-col" isDownload={isDownload}>
            {slices &&
              slices.map((slice, index) => {
                if (
                  "slice_type" in slice &&
                  slice.slice_type in SharedSliceType
                ) {
                  let sliceWrapper = SliceWrapper;
                  if (!isDownload && slice.slice_type === "rich_text") {
                    sliceWrapper = NarrowSliceWrapper;
                  }
                  return (
                    <SharedSlice
                      context={props.pageContext}
                      index={index}
                      key={index}
                      Wrappers={{
                        [WrapperType.narrow]: sliceWrapper,
                        [WrapperType.fullWidth]: SliceWrapper,
                      }}
                      slice={slice as SharedSliceFragment}
                      slice_type={
                        SharedSliceType[
                          slice.slice_type as keyof typeof SharedSliceType
                        ]
                      }
                    />
                  );
                }
              })}
            {author && <AuthorInfo authorInfo={author} />}
          </SliceContent>
          {storiesList && contentPostData?.content_type != "Download" && (
            <FloatingAside>
              <Sticky>
                <VerticalStoriesList verticalStoriesList={storiesList} />
              </Sticky>
            </FloatingAside>
          )}
        </Content>
      </Layout>
    )
  );
};

export default withPrismicPreview(ContentPost as any);
